<script>
  import { createEventDispatcher } from "svelte";
  import backend from "../../../../server";
  import alertify from "alertifyjs";

  export let clientCode = "";
  export let initialValues = null;
  export let platforms = [];
  export let lockEnvironment = false;

  const dispatch = createEventDispatcher();

  let providerUsername = "";
  let providerName = "";
  let providerCode = "";
  let siteId = "";
  let launchUrl = "";
  let environment = "PROD";

  let credentialMode = "NEW"; // NEW | EXISTING

  let loading = false;
  let result = null;
  let formattedResult = "";

  // destino
  let prodClientCode = "";

  // origen (solo cuando se usan credenciales existentes)
  let sourceProdClientCode = "";

  let hydrated = false;

  $: isUsingExistingCredentials = credentialMode === "EXISTING";

  $: if (initialValues && !hydrated) {
    providerName = initialValues.providerName || "";
    providerCode = initialValues.providerCode || "";
    providerUsername = initialValues.providerUsername || "";
    siteId = initialValues.siteId || "";
    launchUrl = initialValues.launchUrl || "";
    environment = "PROD";
    hydrated = true;
  }

  function closeModal() {
    hydrated = false;
    credentialMode = "NEW";
    result = null;
    formattedResult = "";
    prodClientCode = "";
    sourceProdClientCode = "";
    dispatch("close");
  }

  function formatResult(data) {
    if (!data || typeof data !== "object") return "";
    return Object.entries(data)
      .map(([key, value]) => `${key}: ${value ?? ""}`)
      .join("\n");
  }

  function getEffectiveTargetClientCode() {
    return lockEnvironment || isUsingExistingCredentials ? prodClientCode : clientCode;
  }

  async function generateReverseCredentials() {
    const effectiveClientCode = getEffectiveTargetClientCode();

    if (!environment.trim()) {
      alertify.error("Seleccione el environment");
      return;
    }

    if (environment === "PROD" && !effectiveClientCode.trim()) {
      alertify.error("Seleccione la plataforma de producción destino, por favor.");
      return;
    }

    if (isUsingExistingCredentials) {
      if (!effectiveClientCode.trim()) {
        alertify.error("Seleccione la plataforma de producción destino.");
        return;
      }

      if (!sourceProdClientCode.trim()) {
        alertify.error("Seleccione la plataforma de producción origen.");
        return;
      }

      if (effectiveClientCode.trim() === sourceProdClientCode.trim()) {
        alertify.error("La plataforma origen y destino no pueden ser la misma.");
        return;
      }
    } else {
      if (
        !providerName.trim() ||
        !providerUsername.trim() ||
        !providerCode.trim() ||
        !siteId.trim() ||
        !launchUrl.trim()
      ) {
        alertify.error("Complete todos los campos antes de continuar");
        return;
      }

      if (providerCode.trim().length < 2 || providerCode.trim().length > 4) {
        alertify.error("Provider Code debe tener entre 2 y 4 caracteres");
        return;
      }

      if (environment === "PROD" && !effectiveClientCode.trim()) {
        alertify.error("Seleccione un cliente de producción, por favor.");
        return;
      }
    }

    loading = true;
    result = null;
    formattedResult = "";

    try {
      const payload = isUsingExistingCredentials
        ? {
            providerCode: providerCode.trim(),
            environment: "PROD",
            clientCode: effectiveClientCode.trim(),
            useExistingCredentials: true,
            copyFromClientCode: sourceProdClientCode.trim()
          }
        : {
            providerName: providerName.trim(),
            providerUsername: providerUsername.trim(),
            providerCode: providerCode.trim(),
            environment: environment.trim(),
            siteId: siteId.trim(),
            launchUrl: launchUrl.trim(),
            clientCode: effectiveClientCode.trim(),
            useExistingCredentials: false,
            copyFromClientCode: null
          };

      const response = await backend.client.generateReverseCredentials(payload);
      result = response.data;
      formattedResult = formatResult(result);

      alertify.success(
        isUsingExistingCredentials
          ? "✅ Credenciales existentes aplicadas correctamente"
          : "✅ Credenciales generadas correctamente"
      );
    } catch (err) {
      console.error(err);
      alertify.error(
        isUsingExistingCredentials
          ? "❌ Error al usar las credenciales existentes"
          : "❌ Error al generar las credenciales reverse"
      );
    } finally {
      loading = false;
    }
  }

  async function copyResult() {
    if (!formattedResult) {
      alertify.error("No hay texto para copiar");
      return;
    }

    try {
      await navigator.clipboard.writeText(formattedResult);
      alertify.success("✅ Texto copiado");
    } catch (err) {
      console.error(err);
      alertify.error("❌ No se pudo copiar el texto");
    }
  }
</script>

<div
  class="modal"
  on:mousedown|self={closeModal}
  on:keydown={(e) => e.key === "Escape" && closeModal()}
  role="dialog"
  tabindex="-1"
>
  <div class="modal-content" on:click|stopPropagation>
    <div class="title-row">
      <h3>Generar Credenciales Reverse</h3>

      <div class="mode-selector">
        <label for="credentialMode">Modo</label>
        <select
          id="credentialMode"
          bind:value={credentialMode}
          disabled={loading}
        >
          <option value="NEW">Generar credenciales nuevas</option>
          <option value="EXISTING">Usar credenciales existentes</option>
        </select>
      </div>
    </div>

    {#if !isUsingExistingCredentials}
      <label for="providerName">Provider Name</label>
      <input
        id="providerName"
        type="text"
        bind:value={providerName}
        placeholder="Ej. Arrows Edge"
        disabled={loading}
      />

      <label for="providerUsername">Provider Username</label>
      <input
        id="providerUsername"
        type="text"
        bind:value={providerUsername}
        placeholder="Ej. ARRWEDG"
        disabled={loading}
        on:input={(e) => {
          providerUsername = e.target.value.replace(/[^a-zA-Z0-9_]/g, "");
        }}
      />

      <label for="providerCode">Provider Code</label>
      <input
        id="providerCode"
        type="text"
        bind:value={providerCode}
        placeholder="Ej. ared"
        disabled={loading}
        on:input={(e) => {
          providerCode = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
        }}
      />

      {#if providerCode.length < 2 && providerCode}
        <span class="error-text">mínimo 2 caracteres</span>
      {:else if providerCode.length > 4}
        <span class="error-text">máximo 4 caracteres</span>
      {/if}

      <label for="siteId">Client Code / Site ID</label>
      <input
        id="siteId"
        type="text"
        bind:value={siteId}
        placeholder="Ej. USoft-01"
        disabled={loading}
        on:input={(e) => {
          siteId = e.target.value.replace(/[^a-zA-Z0-9_-]/g, "");
        }}
      />

      <label for="launchUrl">Launch URL</label>
      <input
        id="launchUrl"
        type="text"
        bind:value={launchUrl}
        placeholder="Ej. https://universalsoft.games-launch/Games/launch"
        disabled={loading}
      />
    {/if}

    {#if lockEnvironment || isUsingExistingCredentials}
      <label for="prodPlatform">Plataforma de Producción destino</label>
      <select
        id="prodPlatform"
        bind:value={prodClientCode}
        disabled={loading}
      >
        <option value="">Seleccionar plataforma destino...</option>
        {#each platforms as platform}
          <option value={String(platform.code)}>
            {platform.namesite}
          </option>
        {/each}
      </select>
    {/if}

    {#if isUsingExistingCredentials}
      <label for="sourceProdPlatform">Plataforma de Producción origen</label>
      <select
        id="sourceProdPlatform"
        bind:value={sourceProdClientCode}
        disabled={loading}
      >
        <option value="">Seleccionar plataforma origen...</option>
        {#each platforms as platform}
          <option value={String(platform.code)}>
            {platform.namesite}
          </option>
        {/each}
      </select>
    {/if}

    <label for="environment">Environment</label>
    <select
      id="environment"
      bind:value={environment}
      disabled={loading || lockEnvironment}
    >
      <option value="TEST">TEST</option>
      <option value="PROD">PROD</option>
    </select>

    <div class="buttons">
      <button
        on:click={generateReverseCredentials}
        disabled={loading || (!isUsingExistingCredentials && (providerCode.length > 4 || providerCode.length < 2))}
      >
        {#if loading}
          Procesando...
        {:else if isUsingExistingCredentials}
          Usar credenciales existentes
        {:else}
          Generar
        {/if}
      </button>

      <button class="cancel" on:click={closeModal}>Cancelar</button>
    </div>

    {#if formattedResult}
      <div class="result-box">
        <div class="result-header">
          <strong>Resultado</strong>
          <button class="copy-btn" on:click={copyResult}>Copiar texto</button>
        </div>

        <textarea readonly bind:value={formattedResult}></textarea>
      </div>
    {/if}
  </div>
</div>

<style>
  .modal {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background: white;
    padding: 1.5rem;
    border-radius: 8px;
    width: 720px;
    max-width: 95vw;
    max-height: 90vh;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 8px;
  }

  .title-row h3 {
    margin: 0;
  }

  .mode-selector {
    min-width: 280px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  h3 {
    margin: 0 0 0.5rem 0;
  }

  label {
    font-weight: 600;
  }

  input,
  select,
  textarea {
    padding: 8px;
    border-radius: 4px;
    border: 1px solid #ccc;
    font-size: 0.95rem;
  }

  textarea {
    min-height: 280px;
    resize: vertical;
    font-family: monospace;
    line-height: 1.5;
    white-space: pre;
  }

  .buttons {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: 10px;
  }

  button {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    background-color: #007bff;
    color: white;
    font-weight: 500;
  }

  button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .cancel {
    background-color: #dc3545;
  }

  .result-box {
    margin-top: 12px;
    padding: 12px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    background: #fafafa;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .copy-btn {
    background-color: #28a745;
  }

  .error-text {
    color: red;
  }
</style>
